import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  Users,
  KanbanSquare,
  CalendarDays,
  LayoutTemplate,
  Sparkles,
  Zap,
  Inbox as InboxIcon,
  Mail,
  Check,
  Star,
  X,
} from "lucide-react";

const NAV_LINKS = [
  { label: "Product", href: "#features" },
  { label: "Pricing", href: "#pricing" },
  { label: "Customers", href: "#customers" },
];

export default function Home() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[var(--peak-bg)] text-[var(--peak-ink)]" data-testid="landing-page">
      <TopBar user={user} />
      <Hero user={user} />
      <MarqueeStrip />
      <Features />
      <SplitStat />
      <Pricing user={user} />
      <Testimonials />
      <FinalCTA user={user} />
      <Footer />
    </div>
  );
}

/* -------------------------- Top nav -------------------------- */
function TopBar({ user }) {
  return (
    <header className="sticky top-0 z-50 bg-[var(--peak-bg)]/85 backdrop-blur border-b border-[var(--peak-ink)]" data-testid="landing-nav">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 h-16">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[var(--peak-accent)] flex items-center justify-center font-heading font-black text-lg text-white">P</div>
          <span className="font-heading font-black text-xl tracking-tighter">PEAK CRM</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((n) => (
            <a key={n.href} href={n.href} className="text-sm font-semibold hover:text-[var(--peak-accent)] transition-colors">{n.label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to="/dashboard" data-testid="nav-dashboard" className="bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 flex items-center gap-2 transition-colors">
              Open dashboard <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-signin" className="hidden sm:block text-sm font-semibold hover:text-[var(--peak-accent)] transition-colors">Sign in</Link>
              <Link to="/signup" data-testid="nav-signup" className="bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 flex items-center gap-2 transition-colors">
                Start free <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/* -------------------------- Hero -------------------------- */
function Hero({ user }) {
  return (
    <section className="border-b border-[var(--peak-ink)] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-7">
          <div className="section-label text-[var(--peak-accent)] mb-4">CRM · CALENDAR · FUNNELS · AI</div>
          <h1 className="font-heading font-black uppercase text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tighter">
            Every deal.<br />
            Every contact.<br />
            <span className="text-[var(--peak-accent)]">One command centre.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl max-w-xl font-medium text-[var(--peak-ink)]/80 leading-relaxed">
            Peak is the no-nonsense CRM that replaces the four tools you&apos;re currently duct-taping together. Pipelines. Booking. Funnels. Inbox. AI copilot. All in one command centre.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to={user ? "/dashboard" : "/signup"}
              data-testid="hero-cta"
              className="bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white text-sm font-bold uppercase tracking-widest px-8 py-4 flex items-center gap-3 transition-colors"
            >
              {user ? "Open your CRM" : "Start free — no card"} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#pricing" className="text-sm font-semibold underline underline-offset-4 hover:text-[var(--peak-accent)]">See pricing</a>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 text-xs font-mono text-[var(--peak-muted)]">
            <span>✓ 60-second setup</span>
            <span>✓ No credit card</span>
            <span>✓ Cancel anytime</span>
          </div>
        </div>

        {/* Right: mock dashboard preview */}
        <div className="md:col-span-5">
          <MockDashboard />
        </div>
      </div>
    </section>
  );
}

function MockDashboard() {
  return (
    <div className="relative">
      <div className="absolute -inset-2 bg-[var(--peak-blue-deep)] -z-10 rotate-1"></div>
      <div className="bg-white border-2 border-[var(--peak-ink)] p-5" data-testid="hero-mock">
        <div className="flex items-center justify-between mb-4">
          <div className="section-label text-[var(--peak-muted)]">COMMAND CENTRE</div>
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-[var(--peak-ink)]"></div>
            <div className="w-2 h-2 bg-[var(--peak-accent)]"></div>
            <div className="w-2 h-2 bg-[var(--peak-blue-deep)]"></div>
          </div>
        </div>
        <div className="font-heading font-black text-3xl tracking-tighter mb-4">Hey, revenue.</div>
        <div className="grid grid-cols-2 gap-3">
          <MockStat label="Pipeline" value="$248K" trend="+18%" />
          <MockStat label="Booked today" value="12" trend="4 hot" />
          <MockStat label="Leads · 7d" value="341" trend="+62%" />
          <MockStat label="Reply rate" value="47%" trend="AI" />
        </div>
        <div className="mt-4 border border-[var(--peak-ink)] p-3">
          <div className="section-label text-[var(--peak-muted)] mb-2">AI COPILOT</div>
          <div className="text-sm font-medium">&quot;Draft follow-up for the demo no-show...&quot;</div>
          <div className="mt-2 h-1.5 bg-[var(--peak-ink)]/10 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-2/3 bg-[var(--peak-accent)]"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockStat({ label, value, trend }) {
  return (
    <div className="border border-[var(--peak-ink)] p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--peak-muted)]">{label}</div>
      <div className="font-heading font-black text-2xl tracking-tighter mt-1">{value}</div>
      <div className="text-xs font-mono text-[var(--peak-accent)] mt-0.5">{trend}</div>
    </div>
  );
}

/* -------------------------- Marquee logos -------------------------- */
function MarqueeStrip() {
  const items = [
    "GO HIGH LEVEL", "HUBSPOT", "PIPEDRIVE", "SALESFORCE", "CLOSE.IO", "ATTIO", "MONDAY", "COPPER",
  ];
  return (
    <section className="bg-[var(--peak-ink)] text-white border-b border-[var(--peak-ink)] py-4 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center gap-6 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-widest text-white/60 whitespace-nowrap">Replaces →</span>
        {items.map((i) => (
          <span key={i} className="text-xs font-mono text-white/40 line-through decoration-[var(--peak-accent)] decoration-2">
            {i}
          </span>
        ))}
      </div>
    </section>
  );
}

/* -------------------------- Features -------------------------- */
const FEATURES = [
  { icon: Users, title: "Contacts & Leads", body: "Import CSVs, custom fields, AI-scored hotness, activity timelines and de-dupe.", color: "var(--peak-accent)" },
  { icon: KanbanSquare, title: "Pipelines & Deals", body: "Drag-and-drop Kanban with custom stages, revenue rollups and win/lost analytics.", color: "var(--peak-blue-deep)" },
  { icon: CalendarDays, title: "Booking Calendars", body: "Public /book links, conflict prevention, Google Calendar 2-way sync, reminders.", color: "var(--peak-accent)" },
  { icon: LayoutTemplate, title: "Funnels & Landing Pages", body: "Ship pricing pages, lead magnets and testimonials in minutes. Zero code.", color: "var(--peak-blue-deep)" },
  { icon: Zap, title: "Workflow Automation", body: "Multi-step drip campaigns, conditions, delays. Score → tag → email → wait → SMS.", color: "var(--peak-accent)" },
  { icon: InboxIcon, title: "Unified Inbox", body: "IMAP + threaded conversations. Priority Inbox surfaces the hottest 5 every morning.", color: "var(--peak-blue-deep)" },
  { icon: Sparkles, title: "AI Copilot", body: "Claude 4.5 drafts personalized outreach, scores leads and classifies replies as HOT/WARM/COLD.", color: "var(--peak-accent)" },
  { icon: Mail, title: "Email + SMS", body: "Resend + Twilio wired. Send from your domain, track opens, replies and bounces.", color: "var(--peak-blue-deep)" },
];

function Features() {
  return (
    <section id="features" className="border-b border-[var(--peak-ink)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="section-label text-[var(--peak-accent)]">THE STACK</div>
        <h2 className="font-heading font-black text-4xl md:text-6xl tracking-tighter mt-2 max-w-3xl">
          Everything a growth team needs. <span className="text-[var(--peak-muted)]">Zero of the SaaS bloat.</span>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="group border-2 border-[var(--peak-ink)] bg-white p-6 hover:bg-[var(--peak-ink)] hover:text-white transition-colors"
                data-testid={`feature-${f.title.toLowerCase().replace(/\s|&/g, "-")}`}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center mb-4 border-2 border-[var(--peak-ink)] group-hover:border-white"
                  style={{ background: f.color }}
                >
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="font-heading font-black text-lg tracking-tight">{f.title}</div>
                <p className="mt-2 text-sm leading-relaxed text-[var(--peak-muted)] group-hover:text-white/70">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------- Split stat band -------------------------- */
function SplitStat() {
  return (
    <section className="bg-[var(--peak-blue-deep)] text-white border-b border-[var(--peak-ink)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-3 gap-10">
        <StatBig number="4x" label="fewer tools" body="Replaces your CRM, calendar, ESP and funnel builder with one command centre." />
        <StatBig number="60s" label="setup time" body="Sign up, add a contact, ship your first funnel — all before your coffee cools." />
        <StatBig number="$0" label="to start" body="Free forever plan. Upgrade only when you outgrow it. No credit card at signup." />
      </div>
    </section>
  );
}

function StatBig({ number, label, body }) {
  return (
    <div>
      <div className="font-heading font-black text-6xl md:text-7xl tracking-tighter">{number}</div>
      <div className="section-label text-white/70 mt-1">{label}</div>
      <p className="mt-4 text-lg font-medium max-w-sm">{body}</p>
    </div>
  );
}

/* -------------------------- Pricing -------------------------- */
const PLANS = [
  {
    name: "Free",
    price: "$0",
    per: "forever",
    cta: "Start free",
    ctaTone: "outline",
    features: [
      "Up to 250 contacts",
      "1 pipeline · 1 seat",
      "Booking calendar",
      "AI copilot (25 tasks/mo)",
    ],
    caps: ["No email sending", "No SMS", "Peak branding on funnels"],
    featured: false,
  },
  {
    name: "Starter",
    price: "$29",
    per: "seat · month",
    cta: "Start 14-day trial",
    ctaTone: "solid",
    features: [
      "Unlimited contacts",
      "Unlimited pipelines",
      "Unlimited funnels",
      "AI copilot (500 tasks/mo)",
      "Email sending via Resend",
      "Google Calendar sync",
    ],
    caps: [],
    featured: false,
  },
  {
    name: "Pro",
    price: "$99",
    per: "seat · month",
    cta: "Start 14-day trial",
    ctaTone: "primary",
    features: [
      "Everything in Starter",
      "AI copilot · unlimited",
      "Workflow automation",
      "Reply Hotness classifier",
      "Priority Inbox + Auto-Triage",
      "SMS via Twilio",
      "Custom domain funnels",
    ],
    caps: [],
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    per: "annual",
    cta: "Talk to sales",
    ctaTone: "outline",
    features: [
      "Everything in Pro",
      "SSO / SAML",
      "SLA & dedicated support",
      "Custom AI training",
      "White-glove onboarding",
      "Roadmap influence",
    ],
    caps: [],
    featured: false,
  },
];

function Pricing({ user }) {
  return (
    <section id="pricing" className="border-b border-[var(--peak-ink)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="section-label text-[var(--peak-accent)]">PRICING</div>
        <h2 className="font-heading font-black text-4xl md:text-6xl tracking-tighter mt-2">
          Simple. Honest. <span className="text-[var(--peak-muted)]">Cancel anytime.</span>
        </h2>
        <p className="mt-4 max-w-2xl text-lg text-[var(--peak-muted)]">
          Start free. Upgrade the day it starts making you money.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {PLANS.map((p) => (
            <div
              key={p.name}
              data-testid={`plan-${p.name.toLowerCase()}`}
              className={`relative border-2 p-6 flex flex-col ${
                p.featured ? "border-[var(--peak-accent)] bg-[var(--peak-ink)] text-white" : "border-[var(--peak-ink)] bg-white"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-6 bg-[var(--peak-accent)] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                  Most popular
                </span>
              )}
              <div className="font-heading font-black text-2xl tracking-tighter">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-heading font-black text-5xl tracking-tighter">{p.price}</span>
                <span className={`text-xs font-mono ${p.featured ? "text-white/60" : "text-[var(--peak-muted)]"}`}>{p.per}</span>
              </div>
              <ul className="mt-6 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-[var(--peak-accent)]" : "text-[var(--peak-ink)]"}`} />
                    <span>{f}</span>
                  </li>
                ))}
                {p.caps.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-sm opacity-60">
                    <X className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="line-through">{c}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={user ? (p.name === "Free" ? "/dashboard" : p.name === "Enterprise" ? "/signup" : "/settings/billing") : "/signup"}
                data-testid={`plan-cta-${p.name.toLowerCase()}`}
                className={`mt-8 text-center text-xs font-bold uppercase tracking-widest px-4 py-3 flex items-center justify-center gap-2 transition-colors ${
                  p.ctaTone === "primary"
                    ? "bg-[var(--peak-accent)] hover:bg-white hover:text-[var(--peak-accent)] text-white"
                    : p.ctaTone === "solid"
                    ? "bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white"
                    : p.featured
                    ? "border border-white/40 hover:bg-white hover:text-[var(--peak-ink)]"
                    : "border border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white"
                }`}
              >
                {p.cta} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-6 text-xs font-mono text-[var(--peak-muted)]">
          Prices in USD. Emails via Resend, SMS via Twilio. Peak never sells your data.
        </div>
      </div>
    </section>
  );
}

/* -------------------------- Testimonials -------------------------- */
const QUOTES = [
  {
    name: "Marcus L.",
    role: "Founder · Fintech agency",
    quote: "Killed our HubSpot + Calendly + Mailchimp stack in a weekend. The AI copilot alone paid for the year.",
  },
  {
    name: "Priya R.",
    role: "Head of Growth · SaaS",
    quote: "Went from 6% to 18% reply rate on cold outreach after Peak's Reply Hotness classifier reprioritized my inbox.",
  },
  {
    name: "David K.",
    role: "Coach · High-ticket sales",
    quote: "The public booking calendar + auto-triage means I never miss a hot lead again. Peak just quietly runs my funnel.",
  },
];

function Testimonials() {
  return (
    <section id="customers" className="border-b border-[var(--peak-ink)] bg-[var(--peak-bg)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="section-label text-[var(--peak-accent)]">FROM THE FIELD</div>
        <h2 className="font-heading font-black text-4xl md:text-6xl tracking-tighter mt-2 max-w-4xl">
          Operators who stopped renting <span className="text-[var(--peak-muted)]">— and started owning their stack.</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-4 mt-14">
          {QUOTES.map((q) => (
            <blockquote
              key={q.name}
              className="border-2 border-[var(--peak-ink)] bg-white p-6 flex flex-col"
              data-testid={`quote-${q.name.split(" ")[0].toLowerCase()}`}
            >
              <div className="flex gap-0.5">
                {[0,1,2,3,4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-[var(--peak-accent)] text-[var(--peak-accent)]" />)}
              </div>
              <p className="mt-4 text-lg leading-snug font-medium">&ldquo;{q.quote}&rdquo;</p>
              <div className="mt-6 pt-4 border-t border-[var(--peak-ink)]">
                <div className="font-heading font-black text-sm tracking-tight">{q.name}</div>
                <div className="text-xs font-mono text-[var(--peak-muted)]">{q.role}</div>
              </div>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------- Final CTA -------------------------- */
function FinalCTA({ user }) {
  return (
    <section className="bg-[var(--peak-accent)] text-white border-b border-[var(--peak-ink)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <div className="section-label text-white/80">READY?</div>
        <h2 className="font-heading font-black uppercase text-5xl md:text-7xl tracking-tighter mt-4 leading-[0.9]">
          Stop renting your stack.<br />
          Start owning your revenue.
        </h2>
        <p className="mt-8 max-w-2xl mx-auto text-lg font-medium">
          Sign up in 60 seconds. Import contacts. Ship a funnel. Book your first demo — all before lunch.
        </p>
        <Link
          to={user ? "/dashboard" : "/signup"}
          data-testid="final-cta"
          className="inline-flex items-center gap-3 mt-10 bg-[var(--peak-ink)] hover:bg-white hover:text-[var(--peak-accent)] text-white text-sm font-bold uppercase tracking-widest px-10 py-5 transition-colors"
        >
          {user ? "Open your CRM" : "Start free — no card required"} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

/* -------------------------- Footer -------------------------- */
function Footer() {
  return (
    <footer className="bg-[var(--peak-ink)] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[var(--peak-accent)] flex items-center justify-center font-heading font-black text-lg">P</div>
            <span className="font-heading font-black text-xl tracking-tighter">PEAK CRM</span>
          </div>
          <p className="mt-4 text-sm text-white/60 max-w-xs">The command centre for teams that ship. Built for operators, not administrators.</p>
        </div>
        <FooterCol title="Product" links={[["Features", "#features"], ["Pricing", "#pricing"], ["Customers", "#customers"]]} />
        <FooterCol title="Account" links={[["Sign in", "/login"], ["Start free", "/signup"], ["Forgot password", "/forgot-password"]]} internal />
        <FooterCol title="Legal" links={[["Privacy", "#"], ["Terms", "#"], ["DPA", "#"]]} />
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex flex-wrap justify-between text-[10px] font-mono text-white/40 uppercase tracking-widest">
          <span>© 2026 Peak Labs. All rights reserved.</span>
          <span>Made for operators who ship.</span>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links, internal }) {
  return (
    <div>
      <div className="section-label text-white/70">{title}</div>
      <ul className="mt-3 space-y-2">
        {links.map(([label, href]) => (
          <li key={label}>
            {internal ? (
              <Link to={href} className="text-sm text-white/70 hover:text-[var(--peak-accent)] transition-colors">{label}</Link>
            ) : (
              <a href={href} className="text-sm text-white/70 hover:text-[var(--peak-accent)] transition-colors">{label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
